import { useEffect, useState, useCallback } from "react";
import ModalShell from "@/components/ModalShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Folder as FolderIcon, Home, X, ChevronRight } from "lucide-react";

const bus = new EventTarget();
let resolver = null;

/**
 * Open the move-to-folder picker.
 * @param {{ category: string, currentFolderId?: string|null }} opts
 * @returns {Promise<string|null|undefined>} resolves to folder id, null (root), or undefined (cancelled)
 */
export function pickFolder(opts) {
  return new Promise((resolve) => {
    resolver = resolve;
    bus.dispatchEvent(new CustomEvent("open", { detail: opts }));
  });
}

export default function MoveToFolderModal() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [stack, setStack] = useState([]); // [{id, name}] — breadcrumb of current view; last is current view
  const [folders, setFolders] = useState([]);
  const [busy, setBusy] = useState(false);

  const close = useCallback((result) => {
    setOpen(false);
    if (resolver) {
      const r = resolver;
      resolver = null;
      r(result);
    }
  }, []);

  const loadFolders = useCallback(async (parentId, category) => {
    setBusy(true);
    try {
      const { data } = await api.get("/folders", { params: { category, parent_id: parentId || undefined } });
      setFolders(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setFolders([]);
    }
    setBusy(false);
  }, []);

  useEffect(() => {
    function onOpen(e) {
      setOpts(e.detail || {});
      setStack([]);
      setOpen(true);
      loadFolders(null, e.detail.category);
    }
    bus.addEventListener("open", onOpen);
    return () => bus.removeEventListener("open", onOpen);
  }, [loadFolders]);

  function enter(f) {
    setStack((s) => [...s, { id: f.id, name: f.name }]);
    loadFolders(f.id, opts.category);
  }

  function goToBreadcrumb(idx) {
    const next = stack.slice(0, idx + 1);
    setStack(next);
    loadFolders(next.length ? next[next.length - 1].id : null, opts.category);
  }

  function chooseCurrent() {
    const targetId = stack.length ? stack[stack.length - 1].id : null;
    close(targetId);
  }

  const currentId = stack.length ? stack[stack.length - 1].id : null;

  return (
    <ModalShell open={open} onClose={() => close(undefined)} maxWidth="max-w-lg" testId="move-folder-modal">
      <button
        type="button"
        onClick={() => close(undefined)}
        className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm"
        data-testid="move-folder-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Move To</div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight">Choose destination</h2>

      {/* Breadcrumb */}
      <div className="mt-4 flex items-center flex-wrap gap-1.5 font-mono text-[10px] uppercase tracking-widest">
        <button
          type="button"
          onClick={() => goToBreadcrumb(-1)}
          className={`flex items-center gap-1 hover:text-foreground ${stack.length === 0 ? "text-foreground" : "text-muted-foreground"}`}
          data-testid="move-crumb-root"
        >
          <Home className="h-3 w-3" /> Root
        </button>
        {stack.map((s, i) => (
          <span key={s.id} className="flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <button
              type="button"
              onClick={() => goToBreadcrumb(i)}
              className={`hover:text-foreground ${i === stack.length - 1 ? "text-foreground" : "text-muted-foreground"}`}
              data-testid={`move-crumb-${s.id}`}
            >
              {s.name}
            </button>
          </span>
        ))}
      </div>

      {/* Folder list */}
      <div className="mt-4 border border-border rounded-sm min-h-[240px] max-h-[50vh] overflow-y-auto bg-background" data-testid="move-folder-list">
        {busy ? (
          <div className="p-6 text-xs text-muted-foreground animate-pulse">Loading…</div>
        ) : folders.length === 0 ? (
          <div className="p-6 text-xs text-muted-foreground">
            No sub-folders here. Choose &quot;Place Here&quot; to move into {stack.length ? `"${stack[stack.length - 1].name}"` : "the root of this category"}.
          </div>
        ) : (
          folders.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => enter(f)}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-surface-elev flex items-center gap-3 ${
                f.id === opts.currentFolderId ? "opacity-50" : ""
              }`}
              disabled={f.id === opts.currentFolderId}
              data-testid={`move-folder-${f.id}`}
            >
              <FolderIcon className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 text-sm">{f.name}</span>
              {f.id === opts.currentFolderId && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Current</span>
              )}
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          ))
        )}
      </div>

      <div className="flex gap-2 justify-end mt-6">
        <button
          type="button"
          onClick={() => close(undefined)}
          className="border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-surface-elev"
          data-testid="move-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={chooseCurrent}
          disabled={currentId === opts.currentFolderId}
          className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase disabled:opacity-40"
          data-testid="move-place"
        >
          {stack.length ? `Place in "${stack[stack.length - 1].name}"` : "Place at Root"}
        </button>
      </div>
    </ModalShell>
  );
}
